/**
 * @file busy-mixin.ts
 *
 * Manages a ref-counted busy state for the app. Branch components signal
 * intent via `as-app-busy-request` events with `{ key: string, busy: boolean }`.
 * This mixin maintains a Set of active keys and emits `as-app-busy` with
 * `{ busy: boolean }` only when the aggregate state transitions (0→1 or 1→0).
 *
 * This prevents any single component from clearing a busy state that was set
 * by a different operation running concurrently.
 *
 * It also owns the spinner that shows that state - see ensureBusySpinner().
 */
import { type Constructor } from './mixin-types-utils.ts';
import { sendEv } from '../../../data/events.ts';
import { bindElToSysData } from '../../base.ts';
import { _setSysData } from '../../../store/sysDataStore.ts';
import '../../base/as-spinner.ts';

/** Surface added by BusyMixin. */
export interface BusyMixinMembers {
  /** Convenience wrapper around dispatching 'as-app-busy-request' — the same
   *  ref-counted signal any code can already fire directly with
   *  `sendEv(window, 'as-app-busy-request', { detail: { key, busy } })`.
   *  Every `setBusy(key, true)` needs a matching `setBusy(key, false)` from
   *  whoever set it — this mixin does not clear a key on its own, by design
   *  (so one operation can never clear a busy state a different concurrent
   *  operation is still holding). */
  setBusy(key: string, busy: boolean): void;
  /**
   * Resolves once nothing is busy any more; immediately if already idle. After
   * `timeoutMs` it gives up, calls `onTimeout` if given (a chance to abort
   * whatever is stuck) and resolves anyway, so a caller waiting to close or
   * navigate is never held forever.
   */
  waitForIdle(timeoutMs?: number, onTimeout?: () => void): Promise<void>;
  /** Puts an `<as-spinner>` in `#as-main-content` bound to `sysData.busy`, and
   *  returns whether one is there. Idempotent; see the mixin for who calls it. */
  ensureBusySpinner(): boolean;
}

/**
 * Synchronous snapshot of the app's ref-counted busy state, for callers that
 * don't hold the as-app element. The `as-get-app-busy` handler below resolves
 * its callback inline, so the value is readable in the same tick. Returns false
 * when nothing is listening (no as-app yet, or a node-environment test) — same
 * answer as "not busy", which is the safe default for a caller deciding whether
 * to go ahead.
 */
export const isAppBusy = (): boolean => {
  if (typeof window === 'undefined') return false;
  let busy = false;
  sendEv(window, 'as-get-app-busy', {
    detail: { resolve: (detail: { busy: boolean }) => { busy = detail.busy; } }
  });
  return busy;
};

export const BusyMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class BusyMixin extends Base {
    _busyKeys = new Set<string>();

    constructor(...args: any[]) {
      super(...args);
      window.addEventListener('as-app-busy-request', this._handleBusyRequest as EventListener);
      window.addEventListener('as-get-app-busy', this._handleGetBusy as EventListener);
    }

    _handleGetBusy = (ev: CustomEvent<{ resolve: (detail: { busy: boolean; keys: string[] }) => void }>): void => {
      ev.detail.resolve({
        busy: this._busyKeys.size > 0,
        keys: Array.from(this._busyKeys)
      });
    };

    _handleBusyRequest = (ev: CustomEvent<{ key: string; busy: boolean }>): void => {
      const { key, busy } = ev.detail;
      const wasBusy = this._busyKeys.size > 0;
      if (busy) {
        this._busyKeys.add(key);
      } else {
        this._busyKeys.delete(key);
      }
      const isBusy = this._busyKeys.size > 0;

      // Releasing a key nobody held changes nothing and says nothing. The
      // store is written here, at the moment of truth, and the event follows.
      if (isBusy || wasBusy) {
        _setSysData({ busy: isBusy, busyKeys: Array.from(this._busyKeys) });
        sendEv(window, 'as-app-busy', {
          detail: {
            busy: isBusy,
            keys: Array.from(this._busyKeys)
          }
        });
      }
    };

    setBusy(key: string, busy: boolean): void {
      sendEv(window, 'as-app-busy-request', { detail: { key, busy } });
    }

    waitForIdle(timeoutMs = 30000, onTimeout?: () => void): Promise<void> {
      if (this._busyKeys.size === 0) return Promise.resolve();
      return new Promise<void>(resolve => {
        let timer: ReturnType<typeof setTimeout>;
        const done = () => {
          window.removeEventListener('as-app-busy', onBusy as EventListener);
          clearTimeout(timer);
          resolve();
        };
        const onBusy = (ev: CustomEvent<{ busy: boolean }>) => {
          if (ev.detail?.busy === false) done();
        };
        window.addEventListener('as-app-busy', onBusy as EventListener);
        timer = setTimeout(() => { onTimeout?.(); done(); }, timeoutMs);
      });
    }

    /**
     * The app's busy spinner, so no app has to declare and bind its own.
     *
     * Idempotent, and called both from here and from `<as-configurator>.init()`
     * before it decides `useWaitAnim`, so neither has to run first. An app that
     * placed its own spinner keeps it, untouched and unbound. `no-spinner` on
     * `<as-app>` opts out.
     */
    ensureBusySpinner(): boolean {
      const main = document.querySelector('#as-main-content');
      if (!main) return false;
      if ((this as unknown as HTMLElement).hasAttribute('no-spinner')) return false;

      // Direct children only: one nested deeper is inline UI, not this.
      const existing = Array.from(main.children).some(el => el.tagName === 'AS-SPINNER');
      if (existing) return true;

      const spinner = document.createElement('as-spinner');
      spinner.setAttribute('data-as-app-spinner', '');
      main.append(spinner);

      // Applies straight away, so it starts hidden rather than flashing up.
      bindElToSysData(spinner, 'busy', (busy: boolean) => ({ methods: { hide: !busy } }));
      return true;
    }

    async connectedCallback() {
      await super.connectedCallback?.();

      // #as-main-content is parsed after <as-app> opens, so this cannot run
      // inline. init() ensures it too, for the case that gets there first.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.ensureBusySpinner(), { once: true });
      } else {
        this.ensureBusySpinner();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('as-app-busy-request', this._handleBusyRequest as EventListener);
      window.removeEventListener('as-get-app-busy', this._handleGetBusy as EventListener);
      super.disconnectedCallback?.();
    }
  };
